import { ITrackingDTO } from "@hooks/dtos/ITrackingDTO";
import {
  ITrackingHistDTO,
  TrackingHistStatusEnum,
  TrackingHistStatusTypes,
} from "@hooks/dtos/ITrackingHistDTO";
import dayjs from "dayjs";

export const TrackingHistConvertStatus = (
  status: TrackingHistStatusTypes,
): {
  text: string;
  description: string;
  color: string;
} => {
  switch (status) {
    case TrackingHistStatusEnum.PENDENCIA_EMBARQUE:
      return {
        text: "Pedido Criado",
        description:
          "Seu pedido está aguardando liberação ou confirmação financeira para geração do embarque e criação da Invoice (prazo médio: até 48 horas úteis).",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.PENDENCIA_VALIDACAO:
      return {
        text: "Pendência de Validação",
        description:
          "Estamos atualizando as informações de acordo com a exigência, em caso de dúvidas, entre em contato com o atendimento.",
        color: "#ffc107",
      };

    case TrackingHistStatusEnum.SOLICITADO_COLETA:
      return {
        text: "Solicitado Coleta",
        description:
          "O envio foi solicitado e está aguardando a coleta pela transportadora.",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.TRANSITO_INTERNACIONAL:
      return {
        text: "Trânsito Internacional",
        description: "Sua encomenda está em deslocamento entre países.",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.AUTORIZACAO_FNE_COLOMBIA:
      return {
        text: "Autorização FNE Colômbia",
        description:
          "Sua encomenda está sendo analisada na FNE na Colômbia (prazo médio de 5 dias úteis).",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.AUTORIZACAO_EXPORT_MACEDONIA:
      return {
        text: "Autorização Export Macedônia",
        description:
          "A exportação está em processo de autorização pelas autoridades da Macedônia.",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.INSPECAO_ANVISA:
      return {
        text: "Inspeção Anvisa",
        description:
          "Sua encomenda está sendo avaliado pela ANVISA (previsão de 5~10 dias úteis).",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.SEFAZ:
      return {
        text: "SEFAZ",
        description:
          "O documento fiscal está em validação pela SEFAZ (Secretaria da Fazenda do Estado) e o prazo médio é de até 10 dias.",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.TRANSITO_NACIONAL:
      return {
        text: "Trânsito Nacional",
        description:
          "Sua encomenda está em deslocamento e sua previsão de entrega somente será confirmada se não tiver uma ocorrência entre os Estados.",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.TENTATIVA_ENTREGA:
      return {
        text: "Tentativa de Entrega",
        description:
          "Houve tentativa de entrega, mas não foi possível concluir.",
        color: "#5fc4d9",
      };

    case TrackingHistStatusEnum.ENTREGA_EFETIVADA:
      return {
        text: "Entrega Efetivada",
        description: "A entrega foi concluída com sucesso.",
        color: "#28a745",
      };

    case TrackingHistStatusEnum.SOLUCIONADO:
      return {
        text: "Solucionado",
        description: "O pedido está ápto para continuar o percurso.",
        color: "#5fc4d9",
      };

    default:
      return {
        text: "Status Desconhecido",
        description: "O status informado não pôde ser identificado.",
        color: "#5fc4d9",
      };
  }
};

export type TrackingHistIconTypes =
  | "created"
  | "sent"
  | "document" // ANVISA
  | "truck"
  | "delivered";

/**
 * Converts tracking history into 5 delivery status steps
 * @param tracking - Main tracking object
 * @param history - Array with tracking status history
 * @returns Array with the 5 steps formatted for timeline display
 */
export const TrackingHistConvertDeliveryStatus = (
  tracking: ITrackingDTO,
  history: ITrackingHistDTO[],
): {
  iconName: TrackingHistIconTypes;
  label: string;
  date: string;
  active: boolean;
  status: TrackingHistStatusEnum;
  color: string;
}[] => {
  // Create a map of statuses found in history
  const statusMap = new Map<TrackingHistStatusEnum, string>();
  history.forEach((hist) => {
    const enumValue = hist.status as TrackingHistStatusEnum;
    // Store only the first occurrence (oldest) of each status
    if (!statusMap.has(enumValue)) {
      statusMap.set(enumValue, dayjs(hist.createdAt).format("DD/MM/YYYY"));
    }
  });

  // Build the 5 steps
  const steps: Array<{
    iconName: TrackingHistIconTypes;
    label: string;
    date: string;
    active: boolean;
    status: TrackingHistStatusEnum;
    color: string;
  }> = [];

  // --- STEP 1: CREATED (always exists) --- //
  // --- FIX DATE: get the earliest date --- //
  const earliestDate = new Date(
    Math.min(...history.map((item) => new Date(item.createdAt).getTime())),
  );
  const date =
    new Date(tracking.createdAt) < earliestDate
      ? tracking.createdAt
      : earliestDate;

  steps.push({
    iconName: "created",
    label: "Pedido Criado",
    date: dayjs(date).format("DD/MM/YYYY"),
    active: true,
    status: TrackingHistStatusEnum.PENDENCIA_EMBARQUE,
    color: "#5fc4d9",
  });

  // --- STEP 2: DISPATCHED (always displayed, but may be inactive) --- //
  const hasShippingInfo = !!(tracking.company && tracking.companyTrackCode);

  let dispatchDate = "--/--";
  let dispatchActive = false;

  if (hasShippingInfo) {
    // Look for AUTORIZACAO_FNE_COLOMBIA first, then SOLICITADO_COLETA
    dispatchDate =
      statusMap.get(TrackingHistStatusEnum.AUTORIZACAO_FNE_COLOMBIA) ||
      statusMap.get(TrackingHistStatusEnum.SOLICITADO_COLETA) ||
      "--/--";
    dispatchActive = dispatchDate !== "--/--";
  }

  const transIntDate = statusMap.get(
    TrackingHistStatusEnum.TRANSITO_INTERNACIONAL,
  );
  steps.push({
    iconName: "sent",
    label: "Trânsito Internacional",
    date: transIntDate || "--/--",
    active: !!transIntDate,
    status: TrackingHistStatusEnum.TRANSITO_INTERNACIONAL,
    color: hasShippingInfo ? "#5fc4d9" : "#e0e0e0",
  });

  // --- STEP 3: ANVISA INSPECTION --- //
  const anvisaDate = statusMap.get(TrackingHistStatusEnum.INSPECAO_ANVISA);
  steps.push({
    iconName: "document",
    label: "Inspeção ANVISA",
    date: anvisaDate || "--/--",
    active: !!anvisaDate,
    status: TrackingHistStatusEnum.INSPECAO_ANVISA,
    color: anvisaDate ? "#5fc4d9" : "#e0e0e0",
  });

  // --- STEP 4: NATIONAL TRANSIT --- //
  const transitDate = statusMap.get(TrackingHistStatusEnum.TRANSITO_NACIONAL);
  steps.push({
    iconName: "truck",
    label: "Trânsito Nacional",
    date: transitDate || "--/--",
    active: !!transitDate,
    status: TrackingHistStatusEnum.TRANSITO_NACIONAL,
    color: transitDate ? "#5fc4d9" : "#e0e0e0",
  });

  // --- STEP 5: DELIVERED --- //
  const deliveredDate = statusMap.get(TrackingHistStatusEnum.ENTREGA_EFETIVADA);
  steps.push({
    iconName: "delivered",
    label: "Entregue",
    date: deliveredDate || "--/--",
    active: !!deliveredDate,
    status: TrackingHistStatusEnum.ENTREGA_EFETIVADA,
    color: deliveredDate ? "#28a745" : "#e0e0e0",
  });

  // Find the last active step index
  let lastActiveIndex = -1;
  steps.forEach((step, index) => {
    if (step.active) {
      lastActiveIndex = index;
    }
  });

  // Force all steps before the last active to be active
  // If a step is active and previous one is not, activate it and remove its "--/--" date
  return steps.map((step, index) => {
    const shouldBeActive = index <= lastActiveIndex;
    const hasNoDate = step.date === "--/--";

    return {
      ...step,
      active: shouldBeActive,
      color: shouldBeActive
        ? step.status === TrackingHistStatusEnum.ENTREGA_EFETIVADA
          ? "#28a745"
          : "#5fc4d9"
        : "#e0e0e0",
      date: shouldBeActive && hasNoDate ? "" : step.date,
    };
  });
};
